import * as React from 'react'
import { TableCell } from 'components/Table'
import cx from 'classnames'
import { TransactionSourceType } from 'payouts/TransactionSourceType'
import { match } from 'utils/pattern-match-util'
import { formatTransactionReason } from 'payouts/formatters/transaction-formatters'
import { Amount } from 'payouts/components/Amount'
import { UserTransactionReason } from 'payouts/UserTransactionReason'
import styles from './PayoutTransactionsTableCells.sass'
import { TransactionFlag } from '../../TransactionFlag'
import { addSeparators } from '../../../utils/array-util'
import Icon from '../../../components/Icon'
import Tooltip from '../../../components/Tooltip'

interface ContextCellProps {
  source: TransactionSourceType
  userTransactionReason?: UserTransactionReason | null
  userTransactionMemo?: string | null
  jobTransactionJobId?: string | null
  lastFailureDescription?: string | null
}

/**
 * Context cell.
 */
export function ContextCell({
  source,
  userTransactionReason: reason,
  userTransactionMemo: memo,
  jobTransactionJobId: jobId,
  lastFailureDescription,
}: ContextCellProps) {
  const warning = lastFailureDescription && (
    <>
      Previous payout failed
      <br />
      {lastFailureDescription}
    </>
  )
  return (
    <TableCell>
      <div className={styles.contextCell}>
        {match(source, {
          [TransactionSourceType.Unknown]: () => (
            <>
              <ContextCellTitle warning={warning}>
                Unknown source
              </ContextCellTitle>
            </>
          ),
          [TransactionSourceType.User]: () => (
            <>
              <ContextCellTitle warning={warning}>
                {formatTransactionReason(reason)}
              </ContextCellTitle>
              {memo && <ContextCellSubtitle>{memo}</ContextCellSubtitle>}
            </>
          ),
          [TransactionSourceType.Job]: () => (
            <>
              <ContextCellTitle warning={warning}>Job payout</ContextCellTitle>
              <ContextCellSubtitle identifier>{jobId}</ContextCellSubtitle>
            </>
          ),
        })}
      </div>
    </TableCell>
  )
}

function ContextCellTitle({
  children,
  warning,
}: {
  children: React.ReactNode
  warning?: React.ReactNode
}) {
  return (
    <div className={styles.contextCellTitle}>
      {children}
      {warning && (
        <div className={styles.contextCellTitleWarning}>
          <Tooltip placement="top" content={warning}>
            <Icon size={14} name="info-blue" />
          </Tooltip>
        </div>
      )}
    </div>
  )
}

function ContextCellSubtitle({
  children,
  identifier,
}: {
  children: React.ReactNode
  identifier?: boolean
}) {
  return (
    <div
      className={cx(
        styles.contextCellSubtitle,
        identifier && styles.identifier,
      )}
    >
      {children}
    </div>
  )
}

/**
 * Amount Cell
 */
export function AmountCell({
  amount,
  neutral,
}: {
  amount: number
  neutral?: boolean
}) {
  return (
    <TableCell align="right">
      <div className={cx(styles.amountCell)}>
        <Amount neutral={neutral} value={amount} />
      </div>
    </TableCell>
  )
}

/**
 * Flags Cell
 */
export function FlagsCell({ flags }: { flags: TransactionFlag[] }) {
  const flagElements = flags.map((flag) => {
    const [flagClassName, flagText] = match(flag, {
      [TransactionFlag.JobCanceled]: () => [styles.flag, 'Job canceled'],
      [TransactionFlag.Unknown]: () => [styles.flag, '(Unknown flag)'],
      [TransactionFlag.JobPayoutAutoAdjusted]: () => [
        styles.flagInfo,
        'Split auto-adjusted',
      ],
    })
    return (
      <span key={flag} className={flagClassName}>
        {flagText}
      </span>
    )
  })

  return (
    <TableCell>
      <div className={cx(styles.flagsCell)}>
        {addSeparators(flagElements, () => (
          <>, </>
        ))}
      </div>
    </TableCell>
  )
}

/**
 * Identifier Cell
 */
export function IdentifierCell({
  value,
}: {
  value: string | null | undefined
}) {
  return (
    <TableCell>
      <div className={styles.identifierCell}>{value}</div>
    </TableCell>
  )
}
